import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { reportsAPI } from '../../utils/api';

export default function IPDPatientsReport() {
    const today = new Date().toISOString().split('T')[0];
    const [from, setFrom] = useState(today);
    const [to, setTo] = useState(today);
    const [status, setStatus] = useState('All');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');

    const load = async () => {
        setLoading(true);
        try {
            const res = await reportsAPI.ipdPatients(from, to, status);
            setData(res.data);
        } catch { toast.error('Failed to load report'); }
        setLoading(false);
    };

    const print = () => {
        if (!data) return;
        const rows = data.patients.map((p, i) => `
            <tr>
                <td>${i + 1}</td>
                <td>${p.admission_number || '-'}</td>
                <td>${p.mrn || '-'}</td>
                <td>${p.full_name || '-'}</td>
                <td>${p.age || '-'} / ${p.gender || '-'}</td>
                <td>${p.ward_name || '-'} / ${p.bed_number || '-'}</td>
                <td>${p.doctor_name || '-'}</td>
                <td>${p.admission_time ? new Date(p.admission_time).toLocaleString('en-PK') : '-'}</td>
                <td>${p.discharge_time ? new Date(p.discharge_time).toLocaleString('en-PK') : '-'}</td>
                <td>${p.los ?? '-'} days</td>
                <td>${p.discharge_type || p.status || '-'}</td>
            </tr>`).join('');

        const html = `<!DOCTYPE html><html><head><title>IPD Patients Report</title>
        <style>
            body { font-family: Arial, sans-serif; font-size: 11px; margin: 20px; }
            h1 { text-align: center; font-size: 16px; margin: 0; }
            h2 { text-align: center; font-size: 12px; font-weight: normal; margin: 4px 0 12px; }
            table { width: 100%; border-collapse: collapse; }
            th { background: #1e3a5f; color: white; padding: 6px 4px; text-align: left; font-size: 10px; }
            td { padding: 5px 4px; border-bottom: 1px solid #e5e7eb; font-size: 10px; }
            tr:nth-child(even) td { background: #f9fafb; }
            .footer { margin-top: 16px; font-size: 10px; color: #6b7280; }
        </style></head><body>
        <h1>IPD Patients Report</h1>
        <h2>Period: ${from} to ${to} | Status: ${status} | Total: ${data.total} patients | Generated: ${new Date().toLocaleString('en-PK')}</h2>
        <table>
            <thead><tr><th>#</th><th>IPD#</th><th>MR#</th><th>Patient Name</th><th>Age/Gender</th><th>Ward/Bed</th><th>Consultant</th><th>Admission</th><th>Discharge</th><th>LOS</th><th>Status</th></tr></thead>
            <tbody>${rows}</tbody>
        </table>
        <div class="footer">Printed by HIMS — ${new Date().toLocaleString('en-PK')}</div>
        <script>window.onload=()=>{window.print();}</script>
        </body></html>`;
        const w = window.open('', '_blank');
        w.document.write(html);
        w.document.close();
    };

    const filtered = data ? data.patients.filter(p => {
        const q = search.toLowerCase();
        return !q || p.full_name?.toLowerCase().includes(q) || p.mrn?.toLowerCase().includes(q) ||
            p.admission_number?.toLowerCase().includes(q) || p.ward_name?.toLowerCase().includes(q) ||
            p.doctor_name?.toLowerCase().includes(q);
    }) : [];

    const statusBadge = (p) => {
        if (p.status === 'Admitted') return { bg: '#dcfce7', color: '#166534', label: 'Admitted' };
        if (p.discharge_type === 'Death') return { bg: '#fee2e2', color: '#991b1b', label: 'Death' };
        if (p.discharge_type === 'DAMA') return { bg: '#fef3c7', color: '#92400e', label: 'DAMA' };
        return { bg: '#f1f5f9', color: '#374151', label: p.discharge_type || p.status };
    };

    return (
        <div style={{ fontFamily: "'Segoe UI', sans-serif" }}>
            <div style={{ maxWidth: 1200 }}>
                <div style={{ marginBottom: 20 }}>
                    <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#1e3a5f' }}>IPD Patients Report</h1>
                    <p style={{ margin: '4px 0 0', color: '#6b7280', fontSize: 13 }}>Admitted / Discharged patients by date range</p>
                </div>

                {/* Filters */}
                <div style={{ background: 'white', borderRadius: 10, padding: 16, marginBottom: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                    <div>
                        <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>From Date</label>
                        <input type="date" value={from} onChange={e => setFrom(e.target.value)}
                            style={{ padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }} />
                    </div>
                    <div>
                        <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>To Date</label>
                        <input type="date" value={to} onChange={e => setTo(e.target.value)}
                            style={{ padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }} />
                    </div>
                    <div>
                        <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Status</label>
                        <select value={status} onChange={e => setStatus(e.target.value)}
                            style={{ padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, background: 'white' }}>
                            <option value="All">All Patients</option>
                            <option value="Admitted">Currently Admitted</option>
                            <option value="Discharged">Discharged</option>
                        </select>
                    </div>
                    <button onClick={load} disabled={loading}
                        style={{ padding: '9px 20px', background: '#1e3a5f', color: 'white', border: 'none', borderRadius: 6, fontWeight: 600, cursor: 'pointer', fontSize: 13 }}>
                        {loading ? 'Loading...' : 'Load Report'}
                    </button>
                    {data && (
                        <button onClick={print}
                            style={{ padding: '9px 20px', background: '#0369a1', color: 'white', border: 'none', borderRadius: 6, fontWeight: 600, cursor: 'pointer', fontSize: 13 }}>
                            Print Report
                        </button>
                    )}
                </div>

                {/* Search bar - always visible */}
                <div style={{ background: 'white', borderRadius: 10, padding: '10px 16px', marginBottom: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', display: 'flex', alignItems: 'center', gap: 10, border: '1px solid #e5e7eb' }}>
                    <span style={{ fontSize: 16 }}>🔍</span>
                    <input
                        value={search} onChange={e => setSearch(e.target.value)}
                        placeholder="Search by Patient Name, MR#, IPD#, Ward, Consultant..."
                        style={{ flex: 1, border: 'none', outline: 'none', fontSize: 13, color: '#374151' }}
                    />
                    {search && <button onClick={() => setSearch('')} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: 16 }}>✕</button>}
                    {data && search && <span style={{ fontSize: 12, color: '#6b7280', whiteSpace: 'nowrap' }}>{filtered.length} result{filtered.length !== 1 ? 's' : ''}</span>}
                </div>

                {/* Summary cards */}
                {data && (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, marginBottom: 20 }}>
                        {[
                            { label: 'Total Patients', value: data.total, color: '#1e3a5f' },
                            { label: 'Currently Admitted', value: data.patients.filter(p => p.status === 'Admitted').length, color: '#166534' },
                            { label: 'Discharged', value: data.patients.filter(p => p.status === 'Discharged' && p.discharge_type !== 'Death' && p.discharge_type !== 'DAMA').length, color: '#0369a1' },
                            { label: 'Deaths', value: data.patients.filter(p => p.discharge_type === 'Death').length, color: '#991b1b' },
                            { label: 'DAMA', value: data.patients.filter(p => p.discharge_type === 'DAMA').length, color: '#92400e' },
                        ].map(c => (
                            <div key={c.label} style={{ background: 'white', borderRadius: 8, padding: '14px 16px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)', borderLeft: `4px solid ${c.color}` }}>
                                <div style={{ fontSize: 22, fontWeight: 700, color: c.color }}>{c.value}</div>
                                <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>{c.label}</div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Table */}
                {data && (
                    <div style={{ background: 'white', borderRadius: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr style={{ background: '#1e3a5f' }}>
                                        {['#', 'IPD#', 'MR#', 'Patient Name', 'Age/Gender', 'Ward/Bed', 'Consultant', 'Admission', 'Discharge', 'LOS', 'Status'].map(h => (
                                            <th key={h} style={{ padding: '10px 10px', color: 'white', fontWeight: 600, fontSize: 11, textAlign: 'left', whiteSpace: 'nowrap' }}>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {filtered.length === 0 ? (
                                        <tr><td colSpan={11} style={{ textAlign: 'center', padding: 30, color: '#9ca3af' }}>{search ? 'No results match your search' : 'No patients found for selected criteria'}</td></tr>
                                    ) : filtered.map((p, i) => {
                                        const badge = statusBadge(p);
                                        return (
                                            <tr key={p.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                                <td style={{ padding: '9px 10px', fontSize: 12, color: '#6b7280' }}>{i + 1}</td>
                                                <td style={{ padding: '9px 10px', fontSize: 12, fontWeight: 600, color: '#1e3a5f' }}>{p.admission_number || '-'}</td>
                                                <td style={{ padding: '9px 10px', fontSize: 12 }}>{p.mrn || '-'}</td>
                                                <td style={{ padding: '9px 10px', fontSize: 12, fontWeight: 600 }}>{p.full_name}</td>
                                                <td style={{ padding: '9px 10px', fontSize: 12 }}>{p.age || '-'} / {p.gender || '-'}</td>
                                                <td style={{ padding: '9px 10px', fontSize: 12 }}>{p.ward_name} / {p.bed_number}</td>
                                                <td style={{ padding: '9px 10px', fontSize: 12 }}>{p.doctor_name || '-'}</td>
                                                <td style={{ padding: '9px 10px', fontSize: 11, whiteSpace: 'nowrap' }}>{p.admission_time ? new Date(p.admission_time).toLocaleString('en-PK') : '-'}</td>
                                                <td style={{ padding: '9px 10px', fontSize: 11, whiteSpace: 'nowrap' }}>{p.discharge_time ? new Date(p.discharge_time).toLocaleString('en-PK') : '-'}</td>
                                                <td style={{ padding: '9px 10px', fontSize: 12 }}>{p.los ?? '-'} d</td>
                                                <td style={{ padding: '9px 10px' }}>
                                                    <span style={{ background: badge.bg, color: badge.color, padding: '2px 8px', borderRadius: 12, fontSize: 10, fontWeight: 600 }}>{badge.label}</span>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {!data && !loading && (
                    <div style={{ textAlign: 'center', padding: 60, color: '#9ca3af' }}>
                        <div style={{ fontSize: 40, marginBottom: 12 }}>🛏️</div>
                        <div style={{ fontSize: 14 }}>Select date range and click Load Report</div>
                    </div>
                )}
            </div>
        </div>
    );
}
